import React from "react";

import {
  FavoritesSection,
  MyWorkflowsSection,
  WorkflowsNavSection,
  ModuleMenu,
  type ModuleMenuOption,
  type ModuleMenuSection
} from "#app/components/navigation/module-menu";
import { useSidebarNav } from "#app/components/navigation/use-sidebar-nav";
import { useIssueView } from "#app/components/issue-view";
import { useAppData } from "#app/lib/data";
import { useFavoriteIssues } from "#app/lib/hooks/use-favorite-issues";
import { usePermission } from "#app/lib/hooks/use-permission";
import { canEdit } from "#app/lib/utils/access-level";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { CreateWorkflowDialog } from "#app/components/workflows/create-workflow-dialog";

export function WorkflowsModuleMenu() {
  const { navigate } = useSidebarNav();
  const { workflows, refetch: refetchAppData } = useAppData();
  const { issues: favoriteIssues } = useFavoriteIssues();
  const { openCreateIssue } = useIssueView();
  const canCreateWorkflow = usePermission("create.workflows");
  // Post-create destination: the settings editor is gated on
  // manage.edit_workflows, so a create-only user goes to the board instead.
  const canEditWorkflows = usePermission("manage.edit_workflows");
  // "Create project" requires create.items AND a workflow the viewer can
  // create into (edit-or-better). Hidden when no accessible workflow exists.
  const canCreateItems = usePermission("create.items");
  const canCreateProject =
    canCreateItems && workflows.some((w) => canEdit(w.viewerAccessLevel));
  const closeSidebar = useCloseSidebarIfMobile();
  const [createWorkflowOpen, setCreateWorkflowOpen] = React.useState(false);

  // NOTE: order/inclusion here is the instance default — future user
  // preferences will reorder/filter these by section id.
  const sections = React.useMemo<ModuleMenuSection[]>(() => {
    const list: ModuleMenuSection[] = [
      { id: "nav", Component: WorkflowsNavSection },
    ];
    if (favoriteIssues.length > 0) {
      list.push({ id: "favorites", Component: FavoritesSection });
    }
    // Skip My Workflows when there's nothing to show or do — no visible
    // workflows AND no create permission (an empty, actionless accordion).
    if (workflows.length > 0 || canCreateWorkflow) {
      list.push({ id: "my-workflows", Component: MyWorkflowsSection });
    }
    return list;
  }, [favoriteIssues.length, workflows.length, canCreateWorkflow]);

  const options = React.useMemo<ModuleMenuOption[]>(
    () => [
      ...(canCreateWorkflow
        ? [
            {
              id: "create-workflow",
              icon: "workflow" as const,
              label: "Create workflow",
              onClick: () => {
                closeSidebar();
                setCreateWorkflowOpen(true);
              }
            }
          ]
        : []),
      ...(canCreateProject
        ? [
            {
              id: "create-project",
              icon: "plus" as const,
              label: "Create project",
              onClick: () => {
                closeSidebar();
                openCreateIssue({ source: "workflows_menu" });
              }
            }
          ]
        : [])
    ],
    [canCreateWorkflow, canCreateProject, closeSidebar, openCreateIssue]
  );

  return (
    <>
      <ModuleMenu title="Workflows" sections={sections} options={options} />
      {canCreateWorkflow && (
        <CreateWorkflowDialog
          open={createWorkflowOpen}
          onOpenChange={setCreateWorkflowOpen}
          onCreated={async (workflowId) => {
            await refetchAppData();
            navigate(
              canEditWorkflows
                ? `/settings/workflows/${workflowId}`
                : `/workflow/${workflowId}`,
            );
          }}
        />
      )}
    </>
  );
}